/**
 * App-wide UUID minting. Use this instead of calling `crypto.randomUUID()`
 * directly.
 *
 * `crypto.randomUUID()` is only defined in a **secure context** (HTTPS or
 * `localhost`), so it throws "crypto.randomUUID is not a function" on a dev
 * server reached over a plain-`http` LAN IP (common with WSL / device testing).
 * This helper prefers the native call when it exists and otherwise builds an
 * RFC 4122 v4 UUID from `crypto.getRandomValues`, which *is* available in
 * insecure contexts. Either way the result is lowercase hex + hyphens — also a
 * valid `[a-z0-9-]` slug (e.g. for backend idempotency keys). Pure.
 */
export function randomUUID(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();

  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  // Set the RFC 4122 version (4) and variant bits.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
